// =============================================================================
// ShopHandoverPage — sales girl hands her till cash to a driver
// =============================================================================
// One action does two book moves transparently:
//
//   1. Submission JE (posted now):
//        Dr 1150 Cash in Transit
//        Cr <shop drawer>            ← cash leaves the till the moment she signs over the bag
//
//   2. Acknowledge JE (posted later, by finance at HQ):
//        Dr 1101 HQ Cash
//        Cr 1150 Cash in Transit
//
// Both are tied to a single REM-YYYY-##### document the sales girl can show
// the driver (and the driver can show finance). Discrepancies park as DISPUTED
// at the HQ end — finance settles with a Journal Voucher.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { Money } from '../../components/Money';
import { StatusPill } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { cn } from '../../lib/cn';

interface Driver {
  id: string;
  name: string;
  role: string;
  homeShop: { code: string } | null;
}

interface CashCloseToday {
  shopId: string;
  expectedCash: string;
  actualCash: string | null;
  discrepancy: string | null;
  closedAt: string | null;
}

interface RecentHandover {
  id: string;
  number: string;
  status: 'PENDING' | 'ACKNOWLEDGED' | 'DISPUTED';
  source: 'DELIVERY_COD' | 'SHOP_DRAWER';
  claimedAmount: string;
  submittedAt: string;
  driver: { name: string };
  shop: { id: string; code: string; name: string } | null;
}

// Searchable driver combobox — filters the (small, client-side) driver list as
// you type. Shows name + home-shop code only; role is intentionally omitted.
function driverLabel(d: Driver): string {
  return `${d.name}${d.homeShop ? ` · ${d.homeShop.code}` : ''}`;
}

function DriverPicker({
  drivers,
  value,
  onChange,
}: {
  drivers: Driver[];
  value: string;
  onChange: (id: string) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((d) => driverLabel(d).toLowerCase().includes(q));
  }, [drivers, query]);

  const pick = (d: Driver): void => {
    onChange(d.id);
    setQuery(driverLabel(d));
    setOpen(false);
    setActiveIdx(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!open) {
      if (e.key === 'ArrowDown') setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (filtered.length === 0 ? -1 : (i + 1) % filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? filtered.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0 && filtered[activeIdx]) {
      e.preventDefault();
      pick(filtered[activeIdx]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative mt-1">
      <input
        type="text"
        className="kc-input w-full"
        placeholder="Search a driver…"
        value={query}
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIdx(-1);
          if (value) onChange(''); // editing invalidates the prior pick
        }}
        onFocus={(e) => {
          setOpen(true);
          e.target.select();
        }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-surface border border-rule rounded-md shadow-lg max-h-64 overflow-y-auto"
          role="listbox"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-ink-3 italic">No match</div>
          ) : (
            filtered.map((d, i) => (
              <button
                key={d.id}
                type="button"
                onClick={() => pick(d)}
                onMouseEnter={() => setActiveIdx(i)}
                className={cn(
                  'block w-full text-left px-3 py-2 text-sm border-b border-rule-2 last:border-b-0 transition',
                  d.id === value
                    ? 'bg-accent/10 text-ink'
                    : i === activeIdx
                      ? 'bg-paper-2 text-ink'
                      : 'text-ink-2 hover:bg-paper-2 hover:text-ink',
                )}
                role="option"
                aria-selected={d.id === value}
              >
                {driverLabel(d)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function ShopHandoverPage(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const shopId = user?.activeShopId ?? user?.homeShopId ?? null;

  const drivers = useQuery({
    queryKey: ['handover-drivers'],
    queryFn: () => apiGet<Driver[]>('/driver-remittances/_drivers'),
  });

  // Surfacing today's expected cash gives the sales girl an "anchor" — she
  // can compare what the till says to what she's about to hand over.
  const today = useQuery({
    queryKey: ['cash-close-today', shopId],
    queryFn: () => apiGet<CashCloseToday>('/cash-close/today', shopId ? { shopId } : {}),
    enabled: !!shopId,
  });

  // Recent handovers from this shop — same source feed as /remittances, just
  // filtered to ours for context. Sales girl can click through to detail.
  const remList = useQuery({
    queryKey: ['driver-remittances'],
    queryFn: () => apiGet<RecentHandover[]>('/driver-remittances'),
    refetchInterval: 60_000,
  });
  const myRecent = useMemo(
    () =>
      (remList.data ?? [])
        .filter((r) => r.source === 'SHOP_DRAWER' && r.shop?.id === shopId)
        .slice(0, 8),
    [remList.data, shopId],
  );

  const [driverId, setDriverId] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      apiPost<{ id: string; number: string }>('/driver-remittances/drawer', {
        driverId,
        amount: Number(amount),
        notes: notes || undefined,
      }),
    onSuccess: (r) => navigate(`/remittances/${r.id}`),
    onError: (e) => setErr(getApiError(e).message),
  });

  if (!shopId) {
    return (
      <PageScaffold title="Hand cash to driver">
        <Card>
          <EmptyState
            title="No shop assigned"
            description="Cash handover is per-shop. Your account isn't tied to a shop — ask an admin to set your home shop."
          />
        </Card>
      </PageScaffold>
    );
  }

  const expectedNum = Number(today.data?.expectedCash ?? 0);
  const amountNum = Number(amount || 0);
  const variance = amountNum - expectedNum;
  const canSubmit = !!driverId && amountNum > 0 && !submit.isPending;

  return (
    <PageScaffold
      width="form-wide"
      title="Hand cash to driver"
      subtitle={
        <>
          {user?.activeShop?.name ?? user?.homeShop?.name ?? '—'} · Pick the driver, count your drawer, hand over the cash.
          We'll post the books and create a remittance for HQ to acknowledge.
        </>
      }
      footer={
        <Button
          disabled={!canSubmit}
          onClick={() => {
            setErr(null);
            submit.mutate();
          }}
        >
          {submit.isPending ? 'Posting…' : 'Hand cash + post'}
        </Button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card title="Handover">
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-ink-3">Driver</label>
                {drivers.isLoading ? (
                  <div className="text-sm text-ink-3 mt-1">Loading drivers…</div>
                ) : (drivers.data ?? []).length === 0 ? (
                  <div className="text-sm text-ink-3 mt-1">
                    No drivers available — ask admin to add a driver or operations user.
                  </div>
                ) : (
                  <DriverPicker
                    drivers={drivers.data ?? []}
                    value={driverId}
                    onChange={setDriverId}
                  />
                )}
              </div>

              <Input
                label="Cash counted (MUR)"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={expectedNum > 0 ? expectedNum.toFixed(2) : '0.00'}
              />

              {amount && expectedNum > 0 && variance !== 0 && (
                <div className="text-xs text-warning">
                  Variance vs. system expected (
                  <Money value={expectedNum} className="text-current" />
                  ):{' '}
                  <strong>
                    {variance > 0 ? '+' : ''}
                    <Money value={variance} className="text-current" />
                  </strong>
                  {
                    " — finance will be told to expect this amount; if it's wrong, run cash close first."
                  }
                </div>
              )}

              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-ink-3">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. 3 × 1000 notes, 28 × 500, rest in coin"
                  className="kc-input min-h-[80px] w-full text-sm mt-1"
                />
              </div>

              {err && <div className="text-xs text-negative">{err}</div>}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="What this does" bodyClassName="px-5 py-4 text-xs text-ink-2 space-y-2">
            <p>
              Once you submit, the cash is <strong>out of your drawer</strong> and on its way to HQ.
            </p>
            <p>
              The driver gets a printed receipt (REM-…) to carry. HQ counts the cash and confirms it
              arrived — and you're cleared.
            </p>
          </Card>

          {today.data && (
            <Card title="Today">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-ink-3">Expected cash</dt>
                  <dd>
                    <Money value={today.data.expectedCash} />
                  </dd>
                </div>
                {today.data.closedAt && (
                  <div className="flex justify-between">
                    <dt className="text-ink-3">Closed at</dt>
                    <dd className="text-xs text-ink-3">
                      {new Date(today.data.closedAt).toLocaleTimeString()}
                    </dd>
                  </div>
                )}
              </dl>
              <Link
                to="/cash-close"
                className="text-xs text-accent hover:underline mt-3 inline-block"
              >
                → Cash close
              </Link>
            </Card>
          )}
        </div>
      </div>

      <Card title="Recent handovers from this shop" bodyClassName="p-0">
        {remList.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-8 text-sm">Loading…</div>
        ) : myRecent.length === 0 ? (
          <EmptyState title="No handovers yet" description="Your first one will show up here." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">REM</th>
                <th className="px-6 py-2 font-medium">Driver</th>
                <th className="px-6 py-2 font-medium">When</th>
                <th className="px-6 py-2 font-medium text-right">Handed over</th>
                <th className="px-6 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {myRecent.map((r) => {
                const tone =
                  r.status === 'ACKNOWLEDGED'
                    ? 'positive'
                    : r.status === 'DISPUTED'
                      ? 'negative'
                      : 'warning';
                return (
                  <tr key={r.id} className="border-b border-rule-2 hover:bg-paper-2">
                    <td className="px-6 py-3">
                      <Link
                        to={`/remittances/${r.id}`}
                        className="kc-mono text-xs text-ink hover:text-accent"
                      >
                        {r.number}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-ink">{r.driver.name}</td>
                    <td className="px-6 py-3 text-xs text-ink-3">
                      {new Date(r.submittedAt).toLocaleString('en-MU', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Money value={r.claimedAmount} />
                    </td>
                    <td className="px-6 py-3">
                      <StatusPill tone={tone}>{r.status.toLowerCase()}</StatusPill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </PageScaffold>
  );
}
